import { ActionButton, ActionButtonType, Callout } from "@octopusdeploy/design-system-components";
import { logger } from "@octopusdeploy/logging";
import type { LifecycleResource, PhaseResource, EnvironmentResource } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { keyBy } from "lodash";
import * as React from "react";
import LifecycleMap from "~/areas/library/components/Lifecycle/LifecycleMap/LifecycleMap";
import ChangeLifecycleDialog from "~/areas/projects/components/Process/Common/ChangeLifecycleDialog";
import type { ProcessContextModelState } from "~/areas/projects/components/Process/types";
import { isTemplatedProjectProcessIdentifier, isDeploymentProcessIdentifier } from "~/areas/projects/components/Process/types";
import BaseComponent from "~/components/BaseComponent";
import OpenDialogButton from "~/components/Dialog/OpenDialogButton";
import InternalLink from "~/components/Navigation/InternalLink/InternalLink";
import PermissionCheck from "~/components/PermissionCheck/PermissionCheck";
import Note from "~/primitiveComponents/form/Note/Note";
import { ScriptModulesWithActions } from "../../ScriptModules";
import styles from "./style.module.less";
interface SideBarProps {
    includedScriptModules: ScriptModule[];
    lifecyclePreview: LifecycleResource | null;
    environments: EnvironmentResource[];
    onDataChanged: () => Promise<void>;
    processContextState: ProcessContextModelState;
}
interface SideBarState {
    largeLifecyleToggle?: boolean;
}
export interface ScriptModule {
    Id: string;
    Name: string;
}
export default class SideBar extends BaseComponent<SideBarProps, SideBarState> {
    private lifecycleThreshold = 20;
    constructor(props: SideBarProps) {
        super(props);
        this.state = {
            largeLifecyleToggle: true,
        };
    }
    render() {
        const { model: process, processIdentifier } = this.props.processContextState;
        if (!isDeploymentProcessIdentifier(processIdentifier) && !isTemplatedProjectProcessIdentifier(processIdentifier)) {
            logger.error("SideBar must be used in a deployment process.");
            return null;
        }
        if (!process) {
            logger.error("Failed to render SideBar, expecting process to exist.");
            return null;
        }
        const largeLifecycle = this.props.lifecyclePreview &&
            this.props.lifecyclePreview.Phases &&
            (this.props.lifecyclePreview.Phases.length > this.lifecycleThreshold || this.props.lifecyclePreview.Phases.some((p: PhaseResource) => p.OptionalDeploymentTargets.length > this.lifecycleThreshold));
        return (<div>
                <PermissionCheck permission={Permission.LifecycleView} alternate={<Callout type={"information"} title={"Permission required"}>
                            The {Permission.LifecycleView} permission is required to view the Lifecycle
                        </Callout>}>
                    {this.props.lifecyclePreview && (<div>
                            <h4>Lifecycle</h4>
                            <div className={styles.row}>
                                <InternalLink to={links.lifecyclePage.generateUrl({ spaceId: processIdentifier.spaceId, lifecycleId: this.props.lifecyclePreview.Id })}>
                                    <span className={styles.lifeCycleName}>{this.props.lifecyclePreview.Name}</span>
                                </InternalLink>
                                <OpenDialogButton label={"Change"} permission={{
                    permission: Permission.ProjectEdit,
                    project: processIdentifier.projectId,
                    wildcard: true,
                }}>
                                    <ChangeLifecycleDialog projectId={processIdentifier.projectId} saveDone={() => this.props.onDataChanged()}/>
                                </OpenDialogButton>
                            </div>

                            {largeLifecycle && this.props.lifecyclePreview && this.props.lifecyclePreview.Phases && <Note>This lifecycle has many phases / environments</Note>}

                            {largeLifecycle && (<ActionButton onClick={() => this.setState({ largeLifecyleToggle: !this.state.largeLifecyleToggle })} label={this.state.largeLifecyleToggle ? "Show Lifecycle" : "Hide Lifecycle"} type={ActionButtonType.Ternary}/>)}
                            {(!largeLifecycle || !this.state.largeLifecyleToggle) && <LifecycleMap environmentsById={keyBy(this.props.environments, (x) => x.Id)} lifecyclePreview={this.props.lifecyclePreview}/>}
                        </div>)}
                    <Note>
                        Lifecycles can be defined <InternalLink to={links.lifecyclesPage.generateUrl({ spaceId: processIdentifier.spaceId })}>here</InternalLink>
                    </Note>
                </PermissionCheck>
                <hr />
                <PermissionCheck permission={Permission.LibraryVariableSetView} wildcard={true} alternate={<Callout type={"information"} title={"Permission required"}>
                            The {Permission.LibraryVariableSetView} permission is required to view any included script modules
                        </Callout>}>
                    {this.props.includedScriptModules && (<div>
                            <ScriptModulesWithActions spaceId={processIdentifier.spaceId} projectId={processIdentifier.projectId} includedScriptModules={this.props.includedScriptModules} onDataChanged={this.props.onDataChanged}/>
                        </div>)}
                </PermissionCheck>
            </div>);
    }
    static displayName = "SideBar";
}
